import React, { useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, ChevronRight, Flame, Clock, Sparkles, CheckCircle2, Handshake, X, PhoneOff, Repeat2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CAT_ICON = { hot: Flame, overdue: Clock, new: Sparkles };
const CAT_COLOR = { hot: '#DC2626', overdue: '#F59E0B', new: '#0EA5E9', extra: '#71717A' };

const QUICK_ACTIONS = [
  { value: 'appele', label: 'Appelé', icon: Phone, color: '#0055FF' },
  { value: 'rdv_obtenu', label: 'RDV', icon: Handshake, color: '#10B981' },
  { value: 'rappel_demande', label: 'Rappel', icon: Repeat2, color: '#F59E0B' },
  { value: 'injoignable', label: 'Injoignable', icon: PhoneOff, color: '#71717A' },
  { value: 'refus', label: 'Refus', icon: X, color: '#EF4444' },
];

/** Compact widget summarizing today's opportunities — for the Dashboard. */
export default function OpportunitiesTodayWidget() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);

  const reload = () => {
    api.get('/opportunities/today').then(r => setToday(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  if (loading) return null;

  const count = today?.count || 0;
  const isCommercial = user?.role === 'commercial';
  const displayedItems = isCommercial
    ? (today?.items || []).filter(i => (i.commercial_email || '').toLowerCase() === (user?.email || '').toLowerCase())
    : (today?.items || []);

  const markDone = async (oppId, resultStatus) => {
    setBusyId(oppId);
    setOpenMenu(null);
    try {
      await api.post(`/opportunities/${oppId}/done`, { result_status: resultStatus });
      toast.success(`Lead marqué : ${QUICK_ACTIONS.find(a => a.value === resultStatus)?.label}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  if (count === 0 || (isCommercial && displayedItems.length === 0)) {
    return (
      <Card className="border border-[#E5E7EB] shadow-sm" data-testid="opps-today-widget">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#F97316]" />
              <h3 className="text-sm font-semibold">Opportunités du jour</h3>
              <span className="text-xs text-[#71717A]">Aucune opportunité {isCommercial ? 'pour vous' : 'générée'} aujourd'hui</span>
            </div>
            <Link to="/opportunites" className="text-xs text-[#F97316] hover:underline flex items-center gap-1"
              data-testid="opps-widget-cta">
              Ouvrir <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <p className="text-xs text-[#71717A] mt-2">
            Les leads quotidiens sont générés automatiquement du lundi au vendredi à 08h00.
          </p>
        </CardContent>
      </Card>
    );
  }

  // === VUE COMMERCIAL : focus sur ses 3 opps avec checklist d'actions rapides ===
  if (isCommercial) {
    const done = displayedItems.filter(i => i.status === 'done').length;
    const pct = displayedItems.length ? Math.round(100 * done / displayedItems.length) : 0;
    return (
      <Card className="border-2 border-[#F97316]/30 shadow-md bg-gradient-to-br from-[#FFF7ED] to-white" data-testid="opps-today-widget">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  Mes 3 opportunités du jour
                </h3>
                <p className="text-xs text-[#71717A]">
                  Bonjour <strong>{user?.prenom}</strong> · {done}/{displayedItems.length} traités · {pct}% complété
                </p>
              </div>
            </div>
            <Link to="/opportunites"
              className="bg-[#18181B] hover:bg-black text-white text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5"
              data-testid="opps-widget-cta">
              Voir le détail <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden mb-4">
            <div className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#EA580C]" style={{ width: `${pct}%` }} />
          </div>

          <ul className="space-y-2.5">
            {displayedItems.map(opp => {
              const Icon = CAT_ICON[opp.category] || Phone;
              const ps = opp.prospect_snapshot || {};
              const isDone = opp.status === 'done';
              const isOverdue = opp.status === 'overdue';
              return (
                <li key={opp.id}
                  className={`p-3 rounded-xl border flex items-center gap-3 ${isDone ? 'bg-[#F0FDF4] border-[#BBF7D0] opacity-75' : 'bg-white border-[#E5E7EB] hover:border-[#F97316] hover:shadow-md'} transition-all`}
                  data-testid={`opp-row-${opp.id}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: isDone ? '#10B981' : `${CAT_COLOR[opp.category]}20` }}>
                    {isDone ? (
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    ) : (
                      <Icon className="w-5 h-5" style={{ color: CAT_COLOR[opp.category] }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/prospects?prospect=${opp.prospect_id}`}
                      className={`block text-sm font-semibold truncate hover:text-[#F97316] ${isDone ? 'line-through text-[#71717A]' : 'text-[#18181B]'}`}>
                      {ps.enseigne} — {ps.nom_magasin || ps.ville}
                    </Link>
                    <div className="flex items-center gap-2 text-[11px] text-[#71717A] mt-0.5">
                      {ps.telephone && ps.telephone !== '?' && <span>📞 {ps.telephone}</span>}
                      {ps.prenom_dirigeant && <span>👤 {ps.prenom_dirigeant} {ps.nom_dirigeant}</span>}
                      {isOverdue && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#FEF2F2] text-[#DC2626] px-1.5 py-0.5 rounded font-bold">
                          En retard
                        </span>
                      )}
                      {isDone && opp.result_status && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#10B981] text-white px-1.5 py-0.5 rounded font-bold">
                          {QUICK_ACTIONS.find(a => a.value === opp.result_status)?.label || opp.result_status}
                        </span>
                      )}
                    </div>
                  </div>

                  {!isDone && (
                    <div className="relative flex-shrink-0">
                      <button onClick={() => setOpenMenu(openMenu === opp.id ? null : opp.id)}
                        disabled={busyId === opp.id}
                        className="bg-[#F97316] hover:bg-[#EA580C] text-white text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1 transition-colors"
                        data-testid={`opp-action-${opp.id}`}>
                        {busyId === opp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Marquer fait
                      </button>
                      {openMenu === opp.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-10 overflow-hidden min-w-[140px]"
                          data-testid={`opp-menu-${opp.id}`}>
                          {QUICK_ACTIONS.map(a => {
                            const Ai = a.icon;
                            return (
                              <button key={a.value} onClick={() => markDone(opp.id, a.value)}
                                className="w-full px-3 py-2 text-left text-xs hover:bg-[#FFF7ED] flex items-center gap-2 transition-colors"
                                data-testid={`opp-quick-${a.value}`}>
                                <Ai className="w-3.5 h-3.5 flex-shrink-0" style={{ color: a.color }} />
                                <span>{a.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    );
  }

  // === VUE ADMIN : tous les commerciaux (existant conservé) ===
  const grouped = {};
  for (const it of displayedItems) {
    if (!grouped[it.commercial_id]) grouped[it.commercial_id] = { meta: it, items: [] };
    grouped[it.commercial_id].items.push(it);
  }
  const groups = Object.values(grouped);
  const totalDone = displayedItems.filter(i => i.status === 'done').length;
  return (
    <Card className="border-2 border-[#F97316]/20 shadow-sm bg-gradient-to-br from-[#FFF7ED] to-white" data-testid="opps-today-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#F97316] flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Opportunités d'appels du jour</h3>
              <p className="text-[11px] text-[#71717A]">{totalDone}/{displayedItems.length} traités · 3 leads par commercial</p>
            </div>
          </div>
          <Link to="/opportunites"
            className="bg-[#F97316] hover:bg-[#EA580C] text-white text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-colors"
            data-testid="opps-widget-cta">
            Tableau de mission <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map(g => {
            const done = g.items.filter(i => i.status === 'done').length;
            const pct = g.items.length ? Math.round(100 * done / g.items.length) : 0;
            return (
              <div key={g.meta.commercial_id}
                className="rounded-lg border border-[#E5E7EB] p-3 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                    style={{ background: g.meta.commercial_color || '#71717A' }}>
                    {(g.meta.commercial_nom || '').split(' ').map(s => s[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{g.meta.commercial_nom}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] tabular-nums font-semibold" style={{ color: g.meta.commercial_color }}>{pct}%</span>
                      <span className="text-[10px] text-[#71717A]">· {done}/{g.items.length}</span>
                    </div>
                  </div>
                </div>
                <div className="h-1 bg-[#F3F4F6] rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: g.meta.commercial_color }} />
                </div>
                <ul className="space-y-1.5">
                  {g.items.map(opp => {
                    const Icon = CAT_ICON[opp.category] || Phone;
                    const ps = opp.prospect_snapshot || {};
                    const isDone = opp.status === 'done';
                    const isOverdue = opp.status === 'overdue';
                    return (
                      <li key={opp.id} className="text-[11px] flex items-center gap-1.5">
                        {isDone ? (
                          <CheckCircle2 className="w-3 h-3 text-[#10B981] flex-shrink-0" />
                        ) : (
                          <Icon className="w-3 h-3 flex-shrink-0" style={{ color: CAT_COLOR[opp.category] }} />
                        )}
                        <Link to={`/prospects?prospect=${opp.prospect_id}`}
                          className={`flex-1 min-w-0 truncate hover:text-[#F97316] ${isDone ? 'text-[#71717A] line-through' : 'font-medium'}`}>
                          <span className="truncate">{ps.enseigne} {ps.ville}</span>
                        </Link>
                        {isOverdue && (
                          <span className="text-[9px] uppercase tracking-wider bg-[#FEF2F2] text-[#DC2626] px-1 py-0.5 rounded font-semibold flex-shrink-0">
                            Retard
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
